import { Box, Typography, Grid } from '@mui/material';
import { ImageSection } from './image-section.tsx';
import { RegridResponse } from '../../../../models/regrid.model.tsx';
import { useMemo } from 'react';
import { PropertyDetailsResponseData } from '../../../../models/details.models.tsx';
import { translateZoningInfoNumberToString } from '../../../../utils/zoning-info.ts';

interface DataModalProps {
  regridData: RegridResponse | undefined;
  propertyData: PropertyDetailsResponseData | undefined;
  mlsImage?: string;
}

export const DataModalForm = ({
  regridData,
  propertyData,
  mlsImage,
}: DataModalProps) => {
  const permittedLandUses = useMemo(() => {
    const landUseArrays = Object.values(
      regridData?.zoningInformation.permittedLandUses || {}
    );
    let allLandUses: string[] = [];
    landUseArrays.forEach(
      (y: string[]) => (allLandUses = allLandUses.concat(y))
    );
    return allLandUses.join(', ');
  }, [regridData?.zoningInformation]);

  return (
    <>
      <Box sx={{ display: { xs: 'initial', sm: 'none' } }}>
        <ImageSection
          showMlsImage
          propertyData={propertyData}
          mlsImage={mlsImage}
        />
      </Box>
      <Box sx={{ display: { xs: 'none', sm: 'initial' } }}>
        <ImageSection propertyData={propertyData} mlsImage={mlsImage} />
      </Box>
      <Typography
        sx={{
          marginBottom: '12px',
          textAlign: 'left',
          display: 'flex',
        }}
      >
        <Grid
          container
          style={{ flex: 4 }}
          rowSpacing={1}
          columnSpacing={{ xs: 1, sm: 2, md: 3 }}
        >
          <Grid
            size={{
              xs: 12,
              sm: 4,
            }}
          >
            <h3>General</h3>
            <ul>
              <li>
                <b>Object ID:</b> {regridData?.parcelData.objectId}
              </li>
              <li>
                <b>Parcel ID:</b> {regridData?.parcelData.parcelId}
              </li>
              <li>
                <b>County-Provided Acres:</b>{' '}
                {regridData?.parcelData.countyProvidedAcres}
              </li>
              <li>
                <b>County-Provided Parcel Square Feet:</b>{' '}
                {regridData?.parcelData.countyProvidedParcelSquareFeet}
              </li>
              <li>
                <b>Structure on Parcel:</b>{' '}
                {regridData?.parcelData.structureOnParcel}
              </li>
              <li>
                <b>Structure Year built:</b>{' '}
                {regridData?.parcelData.structureYearBuilt}
              </li>
              <li>
                <b>Number of Stories:</b>{' '}
                {regridData?.parcelData.numberOfStories}
              </li>
              <li>
                <b>Number of Living Units:</b>{' '}
                {regridData?.parcelData.numberOfLivingUnits}
              </li>
              <li>
                <b>Number of Rooms:</b> {regridData?.parcelData.numberOfRooms}
              </li>
              <li>
                <b>Structure Style:</b> {regridData?.parcelData.structureStyle}
              </li>
            </ul>
          </Grid>

          <Grid
            size={{
              xs: 12,
              sm: 4,
            }}
          >
            <h3>Ownership</h3>
            <ul>
              <li>
                <b>Owner Type:</b> {regridData?.ownership.ownerType}
              </li>
              <li>
                <b>Owner Name:</b> {regridData?.ownership.ownerName}
              </li>
              <li>
                <b>Owner First Name:</b> {regridData?.ownership.ownerFirstName}
              </li>
              <li>
                <b>Owner Last Name:</b> {regridData?.ownership.ownerLastName}
              </li>
              <li>
                <b>Mailing Address:</b> {regridData?.ownership.mailingAddress}
              </li>
              <li>
                <b>Mailing Address Second Line:</b>{' '}
                {regridData?.ownership.mailingAddressSecondLine}
              </li>
              <li>
                <b>Mailing Address Street Number:</b>{' '}
                {regridData?.ownership.mailingAddressStreetNumber}
              </li>
              <li>
                <b>Mailing Address Street Prefix:</b>{' '}
                {regridData?.ownership.mailingAddressStreetPrefix}
              </li>
              <li>
                <b>Mailing Address Street Name:</b>{' '}
                {regridData?.ownership.mailingAddressStreetName}
              </li>
              <li>
                <b>Mailing Address Street Type:</b>{' '}
                {regridData?.ownership.mailingAddressStreetType}
              </li>
              <li>
                <b>Mailing Address Street Suffix:</b>{' '}
                {regridData?.ownership.mailingAddressStreetSuffix}
              </li>
              <li>
                <b>Mailing Address Unit Number:</b>{' '}
                {regridData?.ownership.mailingAddressUnitNumber}
              </li>
              <li>
                <b>Mailing Address City:</b>{' '}
                {regridData?.ownership.mailingAddressCity}
              </li>
              <li>
                <b>Mailing Address State:</b>{' '}
                {regridData?.ownership.mailingAddressState}
              </li>
              <li>
                <b>Mailing Address ZIP Code:</b>{' '}
                {regridData?.ownership.mailingAddressZipCode}
              </li>
              <li>
                <b>Mailing Address County:</b>{' '}
                {regridData?.ownership.mailingAddressCounty}
              </li>
            </ul>
          </Grid>

          <Grid
            size={{
              xs: 12,
              sm: 4,
            }}
          >
            <h3>Location Information</h3>
            <ul>
              <li>
                <b>Parcel Address:</b>{' '}
                {regridData?.locationInformation.parcelAddress}
              </li>
              <li>
                <b>Parcel Address Second Line:</b>{' '}
                {regridData?.locationInformation.parcelAddressSecondLine}
              </li>
              <li>
                <b>Parcel Address Number:</b>{' '}
                {regridData?.locationInformation.parcelAddressNumber}
              </li>
              <li>
                <b>Parcel Address Prefix:</b>{' '}
                {regridData?.locationInformation.parcelAddressPrefix}
              </li>
              <li>
                <b>Parcel Address Street Name:</b>{' '}
                {regridData?.locationInformation.parcelAddressStreetName}
              </li>
              <li>
                <b>Parcel Address Street Type:</b>{' '}
                {regridData?.locationInformation.parcelAddressStreetType}
              </li>
              <li>
                <b>Parcel Address Street Suffix:</b>{' '}
                {regridData?.locationInformation.parcelAddressStreetSuffix}
              </li>
              <li>
                <b>Parcel Address Unit:</b>{' '}
                {regridData?.locationInformation.parcelAddressUnit}
              </li>
              <li>
                <b>Parcel Address City:</b>{' '}
                {regridData?.locationInformation.parcelAddressCity}
              </li>
              <li>
                <b>Original Parcel Address:</b>{' '}
                {
                  (
                    JSON.parse(
                      regridData?.locationInformation.originalParcelAddress ||
                        '{"address": ""}'
                    ) as { address?: string }
                  )?.address
                }
              </li>
              <li>
                <b>US Census County Subdivision:</b>{' '}
                {regridData?.locationInformation.usCensusCountySubdivision}
              </li>
              <li>
                <b>Parcel Address County:</b>{' '}
                {regridData?.locationInformation.parcelAddressCounty}
              </li>
              <li>
                <b>Parcel Address State:</b>{' '}
                {regridData?.locationInformation.parcelAddressState}
              </li>
              <li>
                <b>Parcel Address ZIP Code:</b>{' '}
                {regridData?.locationInformation.parcelAddressZipCode}
              </li>
              <li>
                <b>5 Digit Parcel Zip Code:</b>{' '}
                {regridData?.locationInformation.parcelAddressZipCode5Digit}
              </li>
              <li>
                <b>Location Name:</b>{' '}
                {regridData?.locationInformation.locationName}
              </li>
              <li>
                <b>Primary Address Source:</b>{' '}
                {regridData?.locationInformation.primaryAddressSource}
              </li>
              <li>
                <b>Block:</b> {regridData?.locationInformation.block}
              </li>
              <li>
                <b>Lot:</b> {regridData?.locationInformation.lot}
              </li>
              <li>
                <b>Neighborhood:</b>{' '}
                {regridData?.locationInformation.neighborhood}
              </li>
              <li>
                <b>Subdivision:</b>{' '}
                {regridData?.locationInformation.subdivision}
              </li>
              <li>
                <b>Latitude:</b> {regridData?.locationInformation.latitude}
              </li>
              <li>
                <b>Longitude:</b> {regridData?.locationInformation.longitude}
              </li>
              <li>
                <b>PLSS Township:</b>{' '}
                {regridData?.locationInformation.plssTownship}
              </li>
              <li>
                <b>PLSS Section:</b>{' '}
                {regridData?.locationInformation.plssSection}
              </li>
              <li>
                <b>PLSS Range:</b> {regridData?.locationInformation.plssRange}
              </li>
            </ul>
          </Grid>

          <Grid
            size={{
              xs: 12,
              sm: 4,
            }}
          >
            <h3>Legal Information</h3>
            <ul>
              <li>
                <b>Legal Description:</b>{' '}
                {regridData?.legalInformation.legalDescription}
              </li>
              <li>
                <b>Plat:</b> {regridData?.legalInformation.plat}
              </li>
              <li>
                <b>Book:</b> {regridData?.legalInformation.book}
              </li>
              <li>
                <b>Page:</b> {regridData?.legalInformation.page}
              </li>
            </ul>
          </Grid>

          <Grid
            size={{
              xs: 12,
              sm: 4,
            }}
          >
            <h3>Flood Zone Data</h3>
            <ul>
              <li>
                <b>FEMA Flood Zone:</b>{' '}
                {regridData?.floodZoneData.femaFloodZone}
              </li>
              <li>
                <b>FEMA Flood Zone Subtype:</b>{' '}
                {regridData?.floodZoneData.femaFloodZoneSubtype}
              </li>
              <li>
                <b>FEMA Flood Zone Data Date:</b>{' '}
                {regridData?.floodZoneData.femaFloodZoneDataDate}
              </li>
            </ul>
          </Grid>

          <Grid
            size={{
              xs: 12,
              sm: 4,
            }}
          >
            <h3>Qualified Opportunity</h3>
            <ul>
              <li>
                <b>Federal Qualified Opportunity Zone:</b>{' '}
                {
                  regridData?.qualifiedOpportunity
                    .federalQualifiedOpportunityZone
                }
              </li>
              <li>
                <b>Qualified Opportunity Zone Tract Number:</b>{' '}
                {
                  regridData?.qualifiedOpportunity
                    .qualifiedOpportunityZoneTractNumber
                }
              </li>
            </ul>
          </Grid>

          <Grid
            size={{
              xs: 12,
              sm: 4,
            }}
          >
            <h3>School Information</h3>
            <ul>
              <li>
                <b>Census Provided Elementary School District:</b>{' '}
                {
                  regridData?.schoolInformation
                    .censusProvidedElementarySchoolDistrict
                }
              </li>
              <li>
                <b>Census Provided Secondary School District:</b>{' '}
                {
                  regridData?.schoolInformation
                    .censusProvidedSecondarySchoolDistrict
                }
              </li>
              <li>
                <b>Census Provided Unified School District:</b>{' '}
                {
                  regridData?.schoolInformation
                    .censusProvidedUnifiedSchoolDistrict
                }
              </li>
            </ul>
          </Grid>

          <Grid
            sx={{
              overflowWrap: 'break-word',
            }}
            size={{
              xs: 12,
              sm: 4,
            }}
          >
            <h3>Zoning Information</h3>
            <ul>
              <li>
                <b>Zoning ID:</b> {regridData?.zoningInformation.zoningId}
              </li>
              <li>
                <b>Zoning:</b> {regridData?.zoningInformation.zoning}
              </li>
              <li>
                <b>Zoning Description:</b>{' '}
                {regridData?.zoningInformation.zoningDescription}
              </li>
              <li>
                <b>Zoning Type:</b> {regridData?.zoningInformation.zoningType}
              </li>
              <li>
                <b>Zoning Subtype:</b>{' '}
                {regridData?.zoningInformation.zoningSubtype}
              </li>
              <li>
                <b>Zoning Objective:</b>{' '}
                {regridData?.zoningInformation.zoningObjective}
              </li>
              <li>
                <b>Zoning Code Link:</b>{' '}
                {regridData?.zoningInformation.zoningCodeLink}
              </li>
              <li>
                <b>Permitted Land Uses:</b> {permittedLandUses}
              </li>
              <li>
                <b>Permitted Land Uses As Of Right:</b>{' '}
                {regridData?.zoningInformation.permittedLandUsesAsOfRight}
              </li>
              <li>
                <b>Permitted Land Uses Conditional:</b>{' '}
                {regridData?.zoningInformation.permittedLandUsesConditional}
              </li>
              <li>
                <b>Minimum Lot Area Square Feet:</b>{' '}
                {translateZoningInfoNumberToString(
                  regridData?.zoningInformation.minimumLotAreaSquareFeet
                )}
              </li>
              <li>
                <b>Minimum Lot Width Feet:</b>{' '}
                {translateZoningInfoNumberToString(
                  regridData?.zoningInformation.minimumLotWidthFeet
                )}
              </li>
              <li>
                <b>Maximum Building Height Feet:</b>{' '}
                {translateZoningInfoNumberToString(
                  regridData?.zoningInformation.maximumBuildingHeightFeet
                )}
              </li>
              <li>
                <b>Maximum Floor Area Ratio (FAR):</b>{' '}
                {translateZoningInfoNumberToString(
                  regridData?.zoningInformation.maximumFloorAreaRatio
                )}
              </li>
              <li>
                <b>Minimum Front Setback Feet:</b>{' '}
                {translateZoningInfoNumberToString(
                  regridData?.zoningInformation.minimumFrontSetbackFeet
                )}
              </li>
              <li>
                <b>Minimum Rear Setback Feet:</b>{' '}
                {translateZoningInfoNumberToString(
                  regridData?.zoningInformation.minimumRearSetbackFeet
                )}
              </li>
              <li>
                <b>Minimum Side Setback Feet:</b>{' '}
                {translateZoningInfoNumberToString(
                  regridData?.zoningInformation.minimumSideSetbackFeet
                )}
              </li>
              <li>
                <b>Maximum Coverage Percentage:</b>{' '}
                {translateZoningInfoNumberToString(
                  regridData?.zoningInformation.maximumCoveragePercentage
                )}
              </li>
              <li>
                <b>Maximum Impervious Coverage Percentage:</b>{' '}
                {translateZoningInfoNumberToString(
                  regridData?.zoningInformation
                    .maximumImperviousCoveragePercentage
                )}
              </li>
              <li>
                <b>Minimum Landscaped Space Percentage:</b>{' '}
                {translateZoningInfoNumberToString(
                  regridData?.zoningInformation.minimumLandscapedSpacePercentage
                )}
              </li>
              <li>
                <b>Minimum Open Space Percentage:</b>{' '}
                {translateZoningInfoNumberToString(
                  regridData?.zoningInformation.minimumOpenSpacePercentage
                )}
              </li>
              <li>
                <b>Maximum Dwelling Units Per Acre:</b>{' '}
                {translateZoningInfoNumberToString(
                  regridData?.zoningInformation.maximumDwellingUnitsPerAcre
                )}
              </li>
              <li>
                <b>Zoning Data Date:</b>{' '}
                {regridData?.zoningInformation.zoningDataDate}
              </li>
              <li>
                <b>Municipality ID:</b>{' '}
                {regridData?.municipality.municipalityId}
              </li>
              <li>
                <b>Municipality Name:</b>{' '}
                {regridData?.municipality.municipalityName}
              </li>
              <li>
                <b>GeoID:</b> {regridData?.municipality.geoId}
              </li>
            </ul>
          </Grid>

          <Grid
            size={{
              xs: 12,
              sm: 4,
            }}
          >
            <h3>Land Use Code</h3>
            <ul>
              <li>
                <b>Land Use Code Activity:</b>{' '}
                {regridData?.landUseCode.lbcsActivity}
              </li>
              <li>
                <b>Land Use Code Description Activity:</b>{' '}
                {regridData?.landUseCode.lbcsActivityDescription}
              </li>
              <li>
                <b>Land Use Code Function:</b>{' '}
                {regridData?.landUseCode.lbcsFunction}
              </li>
              <li>
                <b>Land Use Code Description Function:</b>{' '}
                {regridData?.landUseCode.lbcsFunctionDescription}
              </li>
              <li>
                <b>Land Use Code Structure:</b>{' '}
                {regridData?.landUseCode.lbcsStructure}
              </li>
              <li>
                <b>Land Use Code Description Structure:</b>{' '}
                {regridData?.landUseCode.lbcsStructureDescription}
              </li>
              <li>
                <b>Land Use Code Site:</b> {regridData?.landUseCode.lbcsSite}
              </li>
              <li>
                <b>Land Use Code Description Site:</b>{' '}
                {regridData?.landUseCode.lbcsSiteDescription}
              </li>
              <li>
                <b>Land Use Code Ownership:</b>{' '}
                {regridData?.landUseCode.lbcsOwnership}
              </li>
              <li>
                <b>Land Use Code Description Ownership:</b>{' '}
                {regridData?.landUseCode.lbcsOwnershipDescription}
              </li>
            </ul>
          </Grid>

          <Grid
            size={{
              xs: 12,
              sm: 4,
            }}
          >
            <h3>Additional Regrid Data</h3>
            <ul>
              <li>
                <b>Date of Last Revision:</b>{' '}
                {regridData?.additionalRegridData.dateOfLastRevision}
              </li>
              <li>
                <b>Parcel Path:</b>{' '}
                {regridData?.additionalRegridData.parcelPath}
              </li>
              <li>
                <b>Stable ID Status:</b>{' '}
                {regridData?.additionalRegridData.stableIdStatus}
              </li>
              <li>
                <b>Regrid UUID:</b>{' '}
                {regridData?.additionalRegridData.regridUuid}
              </li>
              <li>
                <b>Parcel Stack UUID:</b>{' '}
                {regridData?.additionalRegridData.parcelStackUuid}
              </li>
              <li>
                <b>Regrid Right-of-Way Parcel Flag:</b>{' '}
                {regridData?.additionalRegridData.regridRightOfWayParcelFlag}
              </li>
              <li>
                <b>Last Modified:</b>{' '}
                {regridData?.additionalRegridData.lastModified}
              </li>
              <li>
                <b>Placekey:</b> {regridData?.additionalRegridData.placekey}
              </li>
              <li>
                <b>PAD-US Public Access Designation:</b>{' '}
                {regridData?.additionalRegridData.padUsPublicAccessDesignation}
              </li>
            </ul>
          </Grid>

          <Grid
            size={{
              xs: 12,
              sm: 4,
            }}
          >
            <h3>Administrative</h3>
            <ul>
              <li>
                <b>Last County Refresh Date:</b>{' '}
                {regridData?.administrative.lastCountyRefreshDate}
              </li>
              <li>
                <b>Source URL:</b> {regridData?.administrative.sourceUrl}
              </li>
            </ul>
          </Grid>
        </Grid>
      </Typography>
    </>
  );
};
